import type { EditorToRendererMessage, RendererToEditorMessage } from './messages';

interface Shown {
  time: number | null;
  category: string | null;
  position: number[];
  rotation: number[];
  scale: number[];
}

const vector = (csv: string | null | undefined): number[] | null => {
  if (!csv) return null;
  const parts = csv.split(',').map(Number);
  return parts.length === 3 && parts.every(Number.isFinite) ? parts : null;
};
const close = (a: number[], b: number[]) => a.every((n, i) => Math.abs(n - b[i]) < 0.0005);
const sameShown = (a: Shown, b: Shown) =>
  a.category === b.category && a.time !== null && b.time !== null && Math.abs(a.time - b.time) < 0.000005 &&
  close(a.position, b.position) && close(a.rotation, b.rotation) && close(a.scale, b.scale);

function poseOf(row: { position?: string | null; rotation?: string | null; scale?: string | null }) {
  const position = vector(row.position);
  const rotation = vector(row.rotation);
  const scale = vector(row.scale);
  return position && rotation && scale ? { position, rotation, scale } : null;
}

// A go_to_position target is the one placement without keyframes: it sits where its own row says
const OWN_POSE_CATEGORIES = new Set(['clipAsset']);

// The renderer applies every keyframe update as it comes, and moves a placement to its row's pose on every update
export class RendererView {
  private readonly keyframes = new Map<string, Shown>();

  outbound(message: EditorToRendererMessage): EditorToRendererMessage | null {
    switch (message.type) {
      case 'assetInstanceUpdated':
        if (OWN_POSE_CATEGORIES.has(message.assetInstance.category ?? '')) return message;
        return { ...message, assetInstance: { ...message.assetInstance, position: null, rotation: null, scale: null } };
      case 'keyframeUpdated': {
        const pose = poseOf(message.keyframe);
        if (!pose) return message;
        const shown = { time: message.keyframe.time ?? null, category: message.keyframe.category ?? null, ...pose };
        const was = this.keyframes.get(message.keyframe.uniqueId);
        if (was && sameShown(was, shown)) return null;
        this.keyframes.set(message.keyframe.uniqueId, shown);
        return message;
      }
      case 'keyframeAdded': {
        // A new keyframe is placed where the renderer chooses, and reported back
        const pose = message.restored ? poseOf(message.keyframe) : null;
        if (pose) this.keyframes.set(message.keyframe.uniqueId, { time: message.keyframe.time ?? null, category: message.keyframe.category ?? null, ...pose });
        else this.keyframes.delete(message.keyframe.uniqueId);
        return message;
      }
      case 'keyframeRemoved':
        this.keyframes.delete(message.keyframeUniqueId);
        return message;
      default:
        return message;
    }
  }

  inbound(message: RendererToEditorMessage): void {
    if (message.type === 'rendererLoaded' || message.type === 'sceneLoaded') {
      this.keyframes.clear();
      return;
    }
    if (message.type !== 'keyframeTransformsEdited') return;
    for (const edit of message.edits) {
      const pose = poseOf(edit);
      if (!pose) continue;
      const was = this.keyframes.get(edit.uniqueId);
      this.keyframes.set(edit.uniqueId, { time: was?.time ?? null, category: was?.category ?? null, ...pose });
    }
  }
}
